import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../axiosSetup";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { formatMoney } from "../utils/formatMoney";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./QuoteDetails.css";
import Breadcrumbs from "../components/Breadcrumbs";
import VocovoLogo from "../assets/VocovoLogoNavbar.png";


export default function QuoteDetails() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(true);


  const formatDate = (val) =>
  val
    ? new Date(val).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "-";

const handleDownloadPDF = async () => {
  const headerEl = document.querySelector("#quote-pdf .pdf-header");
  const infoEl = document.querySelector("#quote-pdf .pdf-section");

  const [headerCanvas, infoCanvas] = await Promise.all([
    html2canvas(headerEl, { scale: 1.5, useCORS: true }),
    html2canvas(infoEl, { scale: 1.5, useCORS: true }),
  ]);

  const pdf = new jsPDF("p", "mm", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();

  const drawImage = (canvas, y) => {
    const w = pageWidth;
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(canvas.toDataURL("PNG"), "PNG", 0, y, w, h);
    return h;
  };

  let y = 0;
  y += drawImage(headerCanvas, y);
  y += 4;
  y += drawImage(infoCanvas, y);

  const body = quote.products.map(p => {
    const qty = p.quantity ?? 0;
    const rrp = p.rrp ?? 0;
    const price =
      p.discountType === "Percentage"
        ? rrp - (rrp * (p.discount || 0)) / 100
        : rrp - (p.discount || 0);

    const subtotal = p.finalPrice ?? price * qty;

    return [
      p.productCode,
      // p.itemType || "-",
      formatMoney(rrp,quote.currency),
p.discountType === "Percentage" ? `${p.discount}%` : formatMoney(p.discount,quote.currency),
formatMoney(price,quote.currency),
qty,
formatMoney(subtotal,quote.currency)
    ];
  });

autoTable(pdf, {
  startY: y + 6,
  margin: { left: 8, right: 10 },
  rowPageBreak: "avoid",
  head: [[
    "Product Code",
    "RRP",
    "Discount",
    "Unit Price",
    "Qty",
    "Subtotal",
  ]],
columnStyles: {
    0: { cellWidth: 50 }, // Product Code
    // 1: { cellWidth: 30 },
    1: { cellWidth: 30, halign: "left" },
    2: { cellWidth: 30, halign: "left" },
    3: { cellWidth: 30, halign: "left" },
    4: { cellWidth: 14, halign: "left" },
    5: { cellWidth: 35, halign: "left" },
  },
  body,

  theme: "grid",

  styles: {
    fontSize: 9,
    cellPadding: 3,
    textColor: [60, 60, 60],
    lineColor: [220, 220, 220],
    lineWidth: 0.3,
  },

  headStyles: {
    fillColor: [74, 20, 118],      // #4A1476
  textColor: [255, 255, 255], 
    fontStyle: "bold",
    halign: "left",
  },

  didDrawPage: (data) => {
    // Draw header ONLY on first page
    if (data.pageNumber === 1) {
      const h = (headerCanvas.height * pageWidth) / headerCanvas.width;
      pdf.addImage(
        headerCanvas.toDataURL("PNG"),
        "PNG",
        0,
        0,
        pageWidth,
        h
      );
    }

    // Footer on every page
    pdf.setFontSize(9);
    pdf.text(
      `© ${new Date().getFullYear()} Fusion Digital`,
      pageWidth / 2,
      pdf.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  },
});
const tableEndY = pdf.lastAutoTable.finalY || (y + 5);
let sumY = tableEndY + 10;
const rightX = pageWidth - 12;   // amount column
const labelX = rightX - 70;     // was 60 → smaller = move right

pdf.setFontSize(10);
pdf.setFont(undefined, "normal");
pdf.text("Subtotal", labelX, sumY);
pdf.text(formatMoney(quote.total, quote.currency), rightX, sumY, { align: "right" });

sumY += 4;
pdf.setLineWidth(0.6);
pdf.line(labelX, sumY, rightX, sumY);

sumY += 6;
pdf.setFontSize(12);
pdf.setFont(undefined, "bold");
pdf.text("Total", labelX, sumY);
pdf.text(formatMoney(quote.total), rightX, sumY, { align: "right" });

pdf.save(`Quote-${quote.id}.pdf`);
};




useEffect(() => {
  const loadQuote = async () => {
    setIsPageLoading(true);
    try {
      const res = await axios.get(`/order/${quoteId}`);
      setQuote(res.data);

      // Set header values correctly
      sessionStorage.setItem("headerTitle", "PO Template Details");
      sessionStorage.setItem(
        "headerSubtitle",
        `${quoteId} • ${formatDate(res.data.createdAt)}`
      );

      // Notify Header to update
      window.dispatchEvent(new Event("header-update"));
    } catch (err) {
      console.error("Failed to load quote", err);
    } finally {
      setIsPageLoading(false);
    }
  };

  loadQuote();
}, [quoteId]);
const formatAddress = (a) => {
  return [
    a.line1,
    a.street,
    a.landmark,
    a.city,
    a.state,
    a.postalCode,
    a.country
  ]
    .filter(Boolean)
    .join(", ");
};
const limitText = (text, limit = 100) => {
  if (!text) return "-";
  return text.length > limit
    ? text.slice(0, limit) + "..."
    : text;
};

const getSalesDisplay = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text && text !== "-" && text.toLowerCase() !== "null" && text.toLowerCase() !== "undefined") {
      return text;
    }
  }
  return "-";
};

  const loadingIcon = <LoadingOutlined style={{ fontSize: 34, color: "#f50087" }} spin />;
  if (isPageLoading || !quote) {
    return (
      <div className="order-details-wrapper" style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
          <Spin indicator={loadingIcon} />
          <span style={{ color: "#f50087", fontWeight: 600 }}>Loading</span>
        </div>
      </div>
    );
  }

  // Helpers with fallbacks
  const customerName = quote.customer?.name || "-";
  const customerId = quote.customer.id|| "-";
  const customerEmail = quote.customer?.email || "-";
const phoneNo = quote.customer?.phoneNo || "-";
  const salesOrderGroup = getSalesDisplay(
    quote.salesOrderGroup,
    quote.customer?.salesOrderGroup,
    quote.orderOptionalFields?.salesOrderGroup
  );
  const salesPerson = getSalesDisplay(
    quote.salesPerson,
    quote.salesPersonName,
    quote.customer?.salesPerson,
    quote.customer?.salesPersonName,
    quote.orderOptionalFields?.salesPerson
  );
  const deliveryAddr = quote.deliveryAddress || null;
 const formatCurrency = (val) =>
  val === undefined || val === null
    ? "-"
    : `${quote.currency} ${Number(val).toFixed(2)}`;



  return (
    <div className="order-details-wrapper">
      
      <Breadcrumbs />
      
      {/* <p className="order-subtitle">
    {quote.quoteName} • {formatDate(quote.createdAt)}
      </p> */}
      <div className="qt-header">
      <div className="qt-header-right">
        <button
          className="action-btn"
          onClick={handleDownloadPDF}
        >
          Download PDF
        </button>
    {String(quote.status).toLowerCase() === "accepted" && (
      <button
        className="create-order-btn"
        onClick={() => navigate(`/orders/add-order?quoteId=${quoteId}`)}
      >
        Create Sales Order
      </button>
    )}
</div>
</div>
      {/* QUOTE SUMMARY */}
      <div className="card">
        <h3>Purchase Order Summary</h3>

        <div className="summary-grid">
  <div className="field">
    <label>Name</label>
    <span>{customerName}</span>
  </div>

  {/* <div className="field">
    <label>Customer ID</label>
    <span>{customerId}</span>
  </div> */}

  <div className="field">
    <label>Email</label>
    <span>{customerEmail}</span>
  </div>

  <div className="field">
    <label>Delivery Address</label>
    <span>
      {deliveryAddr ? limitText(formatAddress(deliveryAddr), 100) : "-"}
    </span>
  </div>

  <div className="field">
    <label>Phone No</label>
    <span>{phoneNo}</span>
  </div>

  <div className="field">
    <label>Delivery Instruction</label>
    <span>
      {quote.deliveryInstruction
        ? limitText(quote.deliveryInstruction, 100)
        : "-"}
    </span>
  </div>

  <div className="field">
    <label>Status</label>
    <span className={`statuses-badge ${String(quote.status || "").toLowerCase()}`}>
      {quote.status || "-"}
    </span>
  </div>

  <div className="field">
    <label>Order Date</label>
    <span>{formatDate(quote.createdAt)}</span>
  </div>

  <div className="field">
    <label>Total Amount</label>
    <span>{formatMoney(quote.total, quote.currency)}</span>
  </div>

  {/* <div className="field">
    <label>Delivery Name</label>
    <span>{quote.deliveryName || "-"}</span>
  </div> */}

  <div className="field">
    <label>Required Date</label>
    <span>{formatDate(quote.requiredDate)}</span>
  </div>

  <div className="field">
    <label>Sales Order Group</label>
    <span>{salesOrderGroup}</span>
  </div>

  <div className="field">
    <label>Sales Person</label>
    <span>{salesPerson}</span>
  </div>

  <div className="field">
    <label>Purchase Order ID</label>
    <span>{quote.purchaseOrderId || "-"}</span>
  </div>
</div>

      </div>

      {/* PRODUCTS */}
      <div className="card">
        <h3>Products</h3>
        <p style={{color: "#9A49CD"}}>
          {Array.isArray(quote.products) ? quote.products.length : 0} items in
          this quote
        </p>

        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
                <tr>
                  <th style={{ width: "23em" }}>Product Code</th>   {/* ⬅ increased */}
                  {/* <th style={{ width: "7em" }}>Item Type</th> */}
                  <th style={{ width: "8em" }}>RRP</th>
                  <th style={{ width: "8em" }}>Discount</th>
                  <th style={{ width: "9em" }}>Price</th>
                  <th style={{ width: "4em", textAlign: "center" }}>Qty</th> {/* ⬅ reduced */}
                  <th style={{ width: "10em" }}>Subtotal</th>
                </tr>
              </thead>


             <tbody>
              {quote.products.map((p, idx) => {
                const qty = p.quantity ?? "-";
                const rrp = p.rrp ?? 0;
                const discount = p.discount ?? 0;

                const priceAfterDiscount =
  p.discountType === "Percentage"
    ? rrp - (rrp * (p.discount || 0)) / 100
    : rrp - (p.discount || 0);

                const subtotal =
                  p.finalPrice ?? priceAfterDiscount * (qty || 0);

                return (
                  <tr key={p.quoteProductId ?? p.id ?? idx}>
                    <td>{p.productCode}</td>
                    {/* <td>{p.itemType || "-"}</td> */}
                    <td>{formatMoney(rrp,quote.currency)}</td>
<td>
  {p.discountType === "Percentage"
    ? `${p.discount}%`
    : formatMoney(p.discount,quote.currency)}
</td>
<td>{formatMoney(priceAfterDiscount,quote.currency)}</td>
<td>{qty}</td>
<td>{formatMoney(subtotal,quote.currency)}</td>
                  </tr>
                );
              })}
            </tbody>


          </table>
        </div>
      </div>
              {/* PDF-ONLY LAYOUT */}
<div id="quote-pdf" className="pdf-layout">
  {/* HEADER */}
  <div className="pdf-header">
  <div className="pdf-header-left">
  <img
    src={VocovoLogo}
    alt="Vocovo"
    className="pdf-logo"
  />
  <p className="pdf-subtitle">Purchase Order</p>
</div>


<div className="pdf-header-right">
  <div className="header-meta">
    <div className="label">Purchase order No</div>
    <div className="value">{quote.id}</div>

    <div className="label">Date</div>
    <div className="value">{formatDate(quote.createdAt)}</div>

    {/* <div className="label">Status</div>
    <div className="value">
      <span className={`pdf-status ${quote.status?.toLowerCase()}`}>
        {quote.status}
      </span>
    </div> */}
  </div>
</div>


</div>


  <hr />

  {/* CUSTOMER DETAILS */}
  <section className="pdf-section">
  <h3>Customer Information</h3>

  <div className="pdf-grid">
    <div>
      <span>Customer Name</span>
      {customerName}
    </div>

    {/* <div>
      <span>Customer ID</span>
      {customerId}
    </div> */}

    <div>
      <span>Email</span>
      {customerEmail}
    </div>

    <div>
      <span>Phone</span>
      {phoneNo}
    </div>

    <div>
      <span>Order Date</span>
      {formatDate(quote.createdAt)}
    </div>

    <div className="wrap-text">
      <span>Delivery Instruction</span>
      {quote.deliveryInstruction || "-"}
    </div>

    <div className="full wrap-text">
      <span>Delivery Address</span>
      {deliveryAddr ? formatAddress(deliveryAddr) : "-"}
    </div>
  </div>
</section>


  {/* PRODUCTS */}
  <section className="pdf-section">
    <h3>Product Details</h3>

    <table className="pdf-table">
      <thead>
        <tr>
          <th className="code-col">Product Code</th>
          <th>Type</th>
          <th>RRP</th>
          <th>Discount</th>
          <th>Unit Price</th>
          <th>Qty</th>
          <th>Subtotal</th>
        </tr>
      </thead>
      <tbody>
        {quote.products.map((p, idx) => {
          const qty = p.quantity ?? 0;
          const rrp = p.rrp ?? 0;
          const price =
            p.discountType === "Percentage"
              ? rrp - (rrp * (p.discount || 0)) / 100
              : rrp - (p.discount || 0);

          const subtotal = p.finalPrice ?? price * qty;

          return (
            <tr key={idx}>
              <td className="code-col wrap-text">{p.productCode}</td>
              {/* <td>{p.itemType || "-"}</td> */}
              <td>{formatCurrency(rrp)}</td>
              <td>
                {p.discountType === "Percentage"
                  ? `${p.discount}%`
                  : formatCurrency(p.discount)}
              </td>
              <td>{formatCurrency(price)}</td>
              <td>{qty}</td>
              <td>{formatCurrency(subtotal)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>

    {/* TOTAL */}
    {/* <div className="pdf-total-box">
      <div><span>Subtotal</span>{formatCurrency(quote.total)}</div>
      <div className="grand-total">
        <span>Total</span>{formatCurrency(quote.total)}
      </div>
    </div> */}
  </section>

  {/* FOOTER */}
  <footer className="pdf-footer">
    <p>This is a system-generated quotation.</p>
    <p>© {new Date().getFullYear()} Fusion Digital</p>
  </footer>
</div>


      {/* <button className="back-btn" onClick={() => navigate("/quotes")}>
        ← Back
      </button> */}
    </div>
  );
}
